import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { BECOME_EXTRA } from "@/data/becomeExtraData";
import { ArrowRight, CheckCircle2, Sparkles, TrendingUp, Briefcase } from "lucide-react";

/**
 * Candidate-side SEO landing page.
 * URL: /devenir-extra-serveur-lyon (etc.)
 *
 * Diff vs LocalLandingPage (business-side):
 *   - CTA principal = "Créer mon profil pro" → /candidat/inscription
 *   - Contenu ciblé sur la démarche auto-entrepreneur, revenus, statut
 *   - Cross-link vers d'autres pages candidat + une page mission correspondante
 */
export default function BecomeExtraPage({ slugProp }) {
    const location = useLocation();
    const slug = slugProp || location.pathname.replace(/^\//, "").replace(/\/+$/, "");
    const data = BECOME_EXTRA[slug];
    const canonicalUrl = `https://renforteo.fr${location.pathname.replace(/\/+$/, "")}`;

    useEffect(() => {
        if (!data) return;
        document.title = data.title;
        const setMeta = (name, content, attr = "name") => {
            let el = document.querySelector(`meta[${attr}="${name}"]`);
            if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
            el.setAttribute("content", content);
        };
        setMeta("description", data.meta_desc);
        setMeta("og:title", data.title, "property");
        setMeta("og:description", data.meta_desc, "property");
        setMeta("og:type", "article", "property");
        setMeta("og:url", canonicalUrl, "property");
        let canonical = document.querySelector('link[rel="canonical"]');
        if (!canonical) { canonical = document.createElement("link"); canonical.setAttribute("rel", "canonical"); document.head.appendChild(canonical); }
        canonical.setAttribute("href", canonicalUrl);
        // JSON-LD FAQPage
        const ldId = "renforteo-become-jsonld";
        let ld = document.getElementById(ldId);
        if (!ld) { ld = document.createElement("script"); ld.id = ldId; ld.type = "application/ld+json"; document.head.appendChild(ld); }
        ld.textContent = JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
                {
                    "@type": "FAQPage",
                    mainEntity: (data.faq || []).map((f) => ({
                        "@type": "Question",
                        name: f.q,
                        acceptedAnswer: { "@type": "Answer", text: f.a },
                    })),
                },
                {
                    "@type": "BreadcrumbList",
                    itemListElement: [
                        { "@type": "ListItem", position: 1, name: "Accueil", item: "https://renforteo.fr/" },
                        { "@type": "ListItem", position: 2, name: "Devenir pro Renforteo", item: "https://renforteo.fr/professionnels" },
                        { "@type": "ListItem", position: 3, name: data.h1.replace(/\.$/, ""), item: canonicalUrl },
                    ],
                },
            ],
        });
    }, [slug, data, canonicalUrl]);

    if (!data) {
        return (
            <div className="min-h-screen"><Header /><div className="p-10 text-center text-slate-500">Page introuvable.</div><Footer /></div>
        );
    }

    return (
        <div className="min-h-screen bg-white" data-testid="become-extra-page">
            <Header />

            {/* HERO */}
            <section className="relative bg-[#0B192C] text-white overflow-hidden noise">
                <div className="absolute inset-0 bg-gradient-to-br from-[#0B192C] via-[#0B192C] to-[#142238]" />
                <div className="relative max-w-5xl mx-auto px-6 md:px-10 py-16 md:py-24">
                    <div className="pill pill-gold mb-5">
                        <Briefcase size={12} /> {data.job_label} · Lyon 2026
                    </div>
                    <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-5">
                        {data.h1}
                    </h1>
                    <p className="text-white/75 max-w-3xl text-base md:text-lg leading-relaxed mb-8">
                        {data.subtitle}
                    </p>
                    <Link to={data.cta_href} className="btn-gold" data-testid="become-hero-cta">
                        {data.cta_label}
                        <ArrowRight className="inline ml-2 -mr-1" size={18} />
                    </Link>
                </div>
            </section>

            {/* BREADCRUMB */}
            <nav className="max-w-5xl mx-auto px-6 md:px-10 py-4 text-xs text-slate-500">
                <Link to="/" className="hover:text-[#F5A623]">Accueil</Link>
                {" › "}
                <Link to="/professionnels" className="hover:text-[#F5A623]">Devenir pro Renforteo</Link>
                {" › "}
                <span className="text-slate-700">{data.h1.replace(/\.$/, "")}</span>
            </nav>

            {/* WHY */}
            <section className="max-w-5xl mx-auto px-6 md:px-10 pb-12">
                <div className="grid md:grid-cols-3 gap-4">
                    {data.why.map((w, i) => (
                        <div key={i} className="sd-card p-6" data-testid={`become-why-${i}`}>
                            <div className="w-10 h-10 rounded-xl bg-[#F5A623]/15 text-[#F5A623] flex items-center justify-center mb-4">
                                <TrendingUp size={20} />
                            </div>
                            <div className="font-display text-lg font-bold text-[#0B192C] mb-2">{w.title}</div>
                            <div className="text-sm text-slate-500 leading-relaxed">{w.desc}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* HOW TO START */}
            <section className="bg-slate-50 border-y border-slate-200">
                <div className="max-w-5xl mx-auto px-6 md:px-10 py-16">
                    <div className="overline mb-3">Comment démarrer</div>
                    <h2 className="font-display text-3xl md:text-4xl tracking-tight text-[#0B192C] mb-8">
                        3 étapes pour recevoir votre première mission.
                    </h2>
                    <div className="space-y-4">
                        {data.how_to_start.map((s, i) => (
                            <div key={i} className="sd-card p-6 flex gap-4" data-testid={`become-step-${i}`}>
                                <div className="w-11 h-11 rounded-full bg-[#F5A623] text-[#0B192C] flex items-center justify-center font-display font-black text-lg shrink-0">
                                    {i + 1}
                                </div>
                                <div>
                                    <div className="font-display text-lg font-bold text-[#0B192C] mb-1">{s.step}</div>
                                    <div className="text-sm text-slate-600 leading-relaxed">{s.desc}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* REVENUE */}
            <section className="max-w-5xl mx-auto px-6 md:px-10 py-16">
                <div className="sd-card p-6 md:p-10 border-l-4 border-[#F5A623] bg-gradient-to-br from-[#F5A623]/5 to-transparent">
                    <div className="overline text-[#F5A623] mb-3">Combien puis-je gagner ?</div>
                    <p className="text-base md:text-lg text-slate-700 leading-relaxed">
                        {data.revenue_hint}
                    </p>
                    <p className="text-xs text-slate-400 mt-4 italic">
                        Estimations 2026 basées sur les fourchettes réelles du marché lyonnais. Résultats variables selon expérience, spécialisation, disponibilités et zones de mobilité déclarées. Charges URSSAF auto-entrepreneur (~22 % du CA) à provisionner soi-même.
                    </p>
                </div>
            </section>

            {/* MISSION TYPES */}
            <section className="max-w-5xl mx-auto px-6 md:px-10 pb-12">
                <div className="overline mb-3">Types de missions</div>
                <h2 className="font-display text-2xl md:text-3xl tracking-tight text-[#0B192C] mb-6">
                    Ce que vous pourrez faire à Lyon
                </h2>
                <div className="grid md:grid-cols-2 gap-3">
                    {data.missions_types.map((m, i) => (
                        <div key={i} className="flex items-start gap-3 sd-card p-4">
                            <CheckCircle2 size={18} className="text-[#F5A623] shrink-0 mt-0.5" />
                            <div className="text-sm text-slate-700 leading-relaxed">{m}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* FAQ */}
            <section className="bg-slate-50 border-y border-slate-200">
                <div className="max-w-4xl mx-auto px-6 md:px-10 py-16">
                    <div className="overline mb-3">Questions fréquentes</div>
                    <h2 className="font-display text-3xl md:text-4xl tracking-tight text-[#0B192C] mb-8">
                        Ce que les futurs pros nous demandent
                    </h2>
                    <div className="space-y-3">
                        {data.faq.map((f, i) => (
                            <details key={i} className="sd-card p-5 md:p-6 group" data-testid={`become-faq-${i}`}>
                                <summary className="cursor-pointer list-none font-display text-lg text-[#0B192C]">
                                    {f.q}
                                </summary>
                                <p className="text-sm text-slate-600 leading-relaxed mt-3">{f.a}</p>
                            </details>
                        ))}
                    </div>
                </div>
            </section>

            {/* LINKED */}
            <section className="max-w-5xl mx-auto px-6 md:px-10 py-16">
                <div className="overline mb-3">Voir aussi</div>
                <h2 className="font-display text-2xl md:text-3xl tracking-tight text-[#0B192C] mb-6">
                    Autres opportunités indépendantes à Lyon
                </h2>
                <div className="grid md:grid-cols-3 gap-4">
                    {data.linked.map((l, i) => (
                        <Link key={i} to={l.href} className="sd-card p-5 hover:border-[#F5A623] transition group">
                            <div className="font-display text-base text-[#0B192C] group-hover:text-[#F5A623] transition">{l.label}</div>
                            <div className="mt-2 text-xs text-slate-400 inline-flex items-center gap-1">Découvrir <ArrowRight size={12} /></div>
                        </Link>
                    ))}
                </div>
            </section>

            {/* CTA FINAL */}
            <section className="bg-[#0B192C] text-white noise">
                <div className="max-w-4xl mx-auto px-6 md:px-10 py-16 text-center">
                    <div className="pill pill-gold mb-5">
                        <Sparkles size={12} /> Inscription gratuite · vérification sous 24 h
                    </div>
                    <h2 className="font-display text-3xl md:text-5xl tracking-tight mb-4">
                        Prêt à recevoir vos premières missions ?
                    </h2>
                    <p className="text-white/70 mb-8 max-w-2xl mx-auto leading-relaxed">
                        Inscription entièrement gratuite. Vérification manuelle sous 24 h ouvrées. Vous ne payez que 8 % de commission uniquement quand vous facturez une mission.
                    </p>
                    <Link to={data.cta_href} className="btn-gold" data-testid="become-final-cta">
                        {data.cta_label}
                        <ArrowRight className="inline ml-2 -mr-1" size={18} />
                    </Link>
                </div>
            </section>

            <Footer />
        </div>
    );
}
